import { X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

export interface PreviewRow {
    question: string
    answer: string
    source: string
    page: string | number
}

interface FilePreviewModalProps {
    isOpen: boolean
    onClose: () => void
    onSave: () => void
    onReplace: () => void
    data: PreviewRow[]
    error?: string[]
    isSaving?: boolean
}

export function FilePreviewModal({ isOpen, onClose, onSave, onReplace, data, error, isSaving }: FilePreviewModalProps) {
    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-xl w-[1000px] max-w-[95%] max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
                <div className="p-6 border-b flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Preview New File</h2>
                        {error && error.length > 0 && (
                            <div className="mt-2 max-h-24 overflow-y-auto pr-2">
                                {error.map((err, idx) => (
                                    <p key={idx} className="text-[#D91F27] text-sm font-medium">{err}</p>
                                ))}
                            </div>
                        )}
                        {error && error.length > 0 && (
                            <Button 
                                variant="outline" 
                                size="sm" 
                                className="mt-2 text-xs h-7"
                                onClick={() => {
                                    const blob = new Blob([error.join('\n')], { type: 'text/plain' })
                                    const url = window.URL.createObjectURL(blob)
                                    const a = document.createElement('a')
                                    a.href = url
                                    a.download = 'validation_errors.txt'
                                    a.click()
                                    window.URL.revokeObjectURL(url)
                                }}
                            >
                                Download Error List
                            </Button>
                        )}
                    </div>
                    <button onClick={onClose}><X className="text-gray-400 hover:text-gray-600" /></button>
                </div>

                <div className="flex-1 overflow-auto p-0">
                    <Table>
                        <TableHeader className="bg-[#D91F27] sticky top-0 z-10">
                            <TableRow className="hover:bg-[#D91F27] border-none">
                                <TableHead className="text-white font-medium w-[25%]">Question</TableHead>
                                <TableHead className="text-white font-medium w-[45%]">Expected answer</TableHead>
                                <TableHead className="text-white font-medium w-[20%]">Source</TableHead>
                                <TableHead className="text-white font-medium w-[10%]">Page</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.map((row, i) => (
                                <TableRow key={i} className="hover:bg-gray-50">
                                    <TableCell className="align-top py-4 text-gray-900">{row.question}</TableCell>
                                    <TableCell className="align-top py-4 text-gray-600">{row.answer}</TableCell>
                                    <TableCell className="align-top py-4 text-gray-900">{row.source}</TableCell>
                                    <TableCell className="align-top py-4 text-gray-900">{row.page}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>

            <div className="p-6 border-t shrink-0 flex justify-end gap-3 bg-gray-50 rounded-b-xl">
                <Button variant="outline" onClick={onClose}>Cancel</Button>
                <Button variant="outline" onClick={onReplace}>Replace File</Button>
                <Button 
                    className="bg-[#D91F27] hover:bg-[#b81a21] text-white disabled:opacity-50 disabled:cursor-not-allowed gap-2" 
                    onClick={onSave}
                    disabled={(error && error.length > 0) || isSaving}
                >
                    Save
                </Button>
            </div>
        </div>
        </div>
    )
}
